import React, { useMemo } from 'react';
import { useStore } from '../store/StoreContext';
import { format, addDays, isToday } from 'date-fns';
import { computeCapacityData, dayPct } from '../lib/capacity';

// CapacityStrip — compact read-only 14-day capacity glance for the
// Dashboard (Jaylen 2026-07-29). Same math as the Capacity page
// (src/lib/capacity.js); clicking anywhere jumps to /#capacity.
//
// Each day: DOW + date, a colored load dot (worst of crew/truck
// utilization), crew count when there's demand, tiny markers for
// quotes circling (violet), tentative move dates (amber "~") and off
// crew (amber). Moves (2026-09-18): supply comes from supplyFor(date) —
// a Closed day is struck and grey, a Full day wears the hatched stripe.

const FULL_STRIPE = 'repeating-linear-gradient(135deg, #fbbf24 0 3px, #fde68a 3px 6px)';

function stripDotCls(pct, hasLoad) {
    if (!hasLoad) return 'bg-slate-200';
    if (pct > 1) return 'bg-rose-500';
    if (pct > 0.8) return 'bg-amber-500';
    if (pct > 0.5) return 'bg-amber-400';
    return 'bg-emerald-500';
}

export default function CapacityStrip() {
    const { jobs, crew, trucks, estimates, clients, availability, moves, dayFlags, truckDowntime } = useStore();

    const cap = useMemo(
        () => computeCapacityData({ jobs, estimates, clients, crew, trucks, availability, moves, dayFlags, truckDowntime }),
        [jobs, estimates, clients, crew, trucks, availability, moves, dayFlags, truckDowntime],
    );

    const days = useMemo(() => {
        const out = [];
        for (let i = 0; i < 14; i++) {
            const d = addDays(new Date(), i);
            const iso = format(d, 'yyyy-MM-dd');
            const load = cap.loadByDate.get(iso);
            const off = cap.offByDate.get(iso);
            const soft = cap.softByDate.get(iso);
            const tentative = cap.tentativeByDate.get(iso);
            const { crewAvail, truckAvail, closed, full } = cap.supplyFor(iso);
            const crewNeeded = load?.crewNeeded || 0;
            const truckNeeded = load?.truckNeeded || 0;
            const pct = dayPct(crewNeeded, crewAvail, truckNeeded, truckAvail);
            out.push({ d, iso, crewNeeded, crewAvail, pct, hasLoad: crewNeeded > 0 || truckNeeded > 0, softCount: soft?.count || 0, tentativeCount: tentative?.count || 0, offCount: off?.count || 0, closed, full });
        }
        return out;
    }, [cap]);

    return (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
                <h2 className="font-lemonmilk text-xs font-bold uppercase tracking-wider text-slate-500">Next 14 Days — Capacity</h2>
                <button onClick={() => { window.location.hash = 'capacity'; }} className="text-xs text-red-600 hover:text-red-700 font-semibold">Full view →</button>
            </div>
            {/* 14 fixed columns — grid-cols-14 isn't a stock Tailwind class,
                so use an inline template. */}
            <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(14, minmax(0, 1fr))' }}>
                {days.map(({ d, iso, crewNeeded, crewAvail, pct, hasLoad, softCount, tentativeCount, offCount, closed, full }) => (
                    <button
                        key={iso}
                        onClick={() => { window.location.hash = 'capacity'; }}
                        title={`${format(d, 'EEE, MMM d')}${closed ? ' — CLOSED' : full ? ' — FULL (no more jobs)' : ''} — crew ${crewNeeded}/${crewAvail}${softCount ? ` · ${softCount} quote${softCount > 1 ? 's' : ''} circling (not booked)` : ''}${tentativeCount ? ` · ${tentativeCount} tentative date${tentativeCount > 1 ? 's' : ''}` : ''}${offCount ? ` · ${offCount} crew off` : ''}`}
                        className={`relative overflow-hidden rounded-lg border px-1 py-1.5 flex flex-col items-center gap-0.5 transition-colors hover:bg-slate-50 ${closed ? 'bg-slate-100 border-slate-200' : isToday(d) ? 'border-red-300 bg-red-50/40' : 'border-slate-100'}`}
                    >
                        {/* Full day: hatched stripe across the top (same mark as the Capacity page / pickers). */}
                        {full && !closed && <span aria-hidden="true" className="absolute inset-x-0 top-0 h-1" style={{ background: FULL_STRIPE }} />}
                        <span className={`text-[9px] font-bold uppercase ${closed ? 'text-slate-300' : 'text-slate-400'}`}>{format(d, 'EEE')}</span>
                        <span className={`text-[11px] font-bold tabular-nums ${closed ? 'text-slate-400 line-through' : isToday(d) ? 'text-red-700' : 'text-slate-700'}`}>{format(d, 'd')}</span>
                        <span className={`w-2.5 h-2.5 rounded-full ${closed ? 'bg-slate-300' : stripDotCls(pct, hasLoad)}`}/>
                        <span className={`text-[9px] tabular-nums leading-none h-3 ${closed ? 'text-slate-400' : 'text-slate-400'}`}>
                            {closed ? 'Closed' : full ? 'Full' : hasLoad ? `${crewNeeded}` : ''}
                        </span>
                        {/* Tiny cells — colored dots only; the tooltip spells
                            out quotes/off in words (Jaylen: no cryptic codes).
                            The "~" is the one glyph the app uses everywhere for
                            a tentative date (moves rule 3). */}
                        <span className="flex items-center gap-0.5 h-2">
                            {softCount > 0 && <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />}
                            {tentativeCount > 0 && <span className="text-[9px] font-black leading-none text-amber-600" style={{ marginTop: -3 }}>~</span>}
                            {offCount > 0 && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />}
                        </span>
                    </button>
                ))}
            </div>
        </div>
    );
}
